import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Plus, Pencil, Trash2, Printer, FileSignature, Package, Upload, Download } from "lucide-react";
import { useAuth } from "@/AuthContext";
import { API } from "@/config";

export const AgreementView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [agreement, setAgreement] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    fetchAgreement();
    fetchLogo();
  }, [id]);

  const fetchAgreement = async () => {
    try {
      const response = await axios.get(`${API}/agreements/by-invoice/${id}`);
      setAgreement(response.data);
    } catch (error) {
      toast.error("Error fetching agreement");
    }
  };

  const fetchLogo = async () => {
    try {
      const response = await axios.get(`${API}/logo`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      setLogoUrl(url);
    } catch (error) {
      console.log("No logo uploaded yet");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (!agreement) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between print:hidden">
        <h1 className="text-4xl font-bold" data-testid="agreement-view-title">Customer Purchase Agreement</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/invoices/${agreement.invoice_id}`)} data-testid="back-to-invoice-btn">
            Back to Invoice
          </Button>
          <Button onClick={handlePrint} data-testid="print-agreement-btn">
            <Printer className="h-4 w-4 mr-2" />
            Print Agreement
          </Button>
        </div>
      </div>

      <Card className="print:shadow-none" data-testid="agreement-card">
        <CardContent className="p-8">
          <div className="space-y-8">
            {/* Header */}
            <div className="text-center">
              {logoUrl && (
                <img src={logoUrl} alt="Company Logo" className="h-20 mx-auto mb-4" data-testid="agreement-logo" />
              )}
              <h2 className="text-3xl font-bold">CUSTOMER PURCHASE AGREEMENT</h2>
              <div className="text-sm text-gray-600 mt-2">Agreement for Invoice: {agreement.invoice_number}</div>
            </div>

            {/* Agreement Content */}
            <div className="prose max-w-none" data-testid="agreement-content">
              <pre className="whitespace-pre-wrap font-sans">{agreement.content}</pre>
            </div>

            {/* Signature Section */}
            <div className="border-t-2 border-gray-800 pt-8 mt-8">
              <h3 className="font-bold text-lg mb-4">ACCEPTANCE</h3>
              <p className="mb-8 text-sm leading-relaxed">
                By signing below, the Purchaser acknowledges that they have read, understood, and agree to be bound by the terms and conditions set forth in this Customer Purchase Agreement.
              </p>
              
              {/* Message on screen */}
              <div className="print:hidden">
                <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-900 font-semibold mb-2">📄 Signature Required</p>
                  <p className="text-blue-800">Please print this agreement and sign in the designated area below.</p>
                </div>
              </div>

              {/* Wet Signature Section - Visible on both screen and print */}
              <div className="mt-8">
                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <div className="border-b-2 border-gray-800 mb-2 h-16"></div>
                    <div className="text-sm font-semibold">Purchaser Signature</div>
                  </div>
                  <div>
                    <div className="border-b-2 border-gray-800 mb-2 h-16"></div>
                    <div className="text-sm font-semibold">Date</div>
                  </div>
                </div>
                
                <div className="mt-8">
                  <div className="border-b-2 border-gray-800 mb-2 h-12 w-2/3"></div>
                  <div className="text-sm font-semibold">Print Name</div>
                </div>

                <div className="mt-8">
                  <div className="border-b-2 border-gray-800 mb-2 h-12 w-2/3"></div>
                  <div className="text-sm font-semibold">Title/Position</div>
                </div>

                <div className="mt-8">
                  <div className="border-b-2 border-gray-800 mb-2 h-12 w-2/3"></div>
                  <div className="text-sm font-semibold">Company/Organization</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

